import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import { Building2, PlusCircle } from 'lucide-react'
import { createEmpresaAdmin, fetchAdminEmpresas } from '../../api'
import { PageHeader } from '../../components/PageHeader'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Skeleton } from '../../components/ui/skeleton'
import { Card } from '../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'

const emptyForm = {
  nombre: '',
  email_contacto: '',
  owner_nombre: '',
  owner_email: '',
  owner_password: '',
  sociedad_nombre: '',
}

export function EmpresasPage() {
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: ['admin', 'empresas'], queryFn: fetchAdminEmpresas })

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () =>
      createEmpresaAdmin({ ...form, email_contacto: form.email_contacto || undefined }),
    onSuccess: (empresa) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'empresas'] })
      setOpen(false)
      setForm(emptyForm)
      toast.success(`Empresa "${empresa.nombre}" creada — pasale el email y la contraseña al Propietario.`)
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('Ya existe una cuenta con ese email de Propietario.')
      } else {
        setError('No se pudo crear la Empresa. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Building2}
        title="Empresas"
        description={
          query.data
            ? `${query.data.length} ${query.data.length === 1 ? 'Empresa registrada' : 'Empresas registradas'} en la plataforma.`
            : 'Todas las cuentas que pagan (o pagarán) la plataforma.'
        }
        action={
          <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setError(null) }}>
            <DialogTrigger asChild>
              <Button>
                <PlusCircle className="size-4" />
                Crear empresa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>Crear Empresa</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="empresa-nombre">Nombre de la Empresa</Label>
                  <Input
                    id="empresa-nombre"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="empresa-email">Email de contacto (opcional)</Label>
                  <Input
                    id="empresa-email"
                    type="email"
                    value={form.email_contacto}
                    onChange={(e) => setForm({ ...form, email_contacto: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="sociedad-nombre">Nombre de la primera Sociedad</Label>
                  <Input
                    id="sociedad-nombre"
                    required
                    value={form.sociedad_nombre}
                    onChange={(e) => setForm({ ...form, sociedad_nombre: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="owner-nombre">Nombre del Propietario</Label>
                  <Input
                    id="owner-nombre"
                    required
                    value={form.owner_nombre}
                    onChange={(e) => setForm({ ...form, owner_nombre: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="owner-email">Email del Propietario</Label>
                  <Input
                    id="owner-email"
                    type="email"
                    required
                    value={form.owner_email}
                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="owner-password">Contraseña del Propietario</Label>
                  <Input
                    id="owner-password"
                    type="text"
                    required
                    minLength={8}
                    value={form.owner_password}
                    onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Mínimo 8 caracteres. Pasale este email y contraseña al Propietario.
                  </p>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending}>
                    Crear empresa
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="overflow-hidden py-0">
        {query.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-5/6" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        )}
        {query.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar las empresas.</p>}
        {query.data && query.data.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <Building2 className="size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Todavía no hay ninguna Empresa registrada.</p>
          </div>
        )}
        {query.data && query.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Sociedades</TableHead>
                <TableHead className="text-right">Usuarios</TableHead>
                <TableHead>Alta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((empresa) => (
                <TableRow key={empresa.id}>
                  <TableCell className="font-medium">
                    <Link to={`/admin/empresas/${empresa.id}`} className="hover:underline">
                      {empresa.nombre}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{empresa.sociedades_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{empresa.usuarios_count}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(empresa.created_at).toLocaleDateString('es-CR')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}
